if (!customElements.get('product-family-selector')) {
  class ProductFamilySelector extends HTMLElement {
    connectedCallback() {
      this.familyKey = this.dataset.familyKey;
      this.familyTitle = this.dataset.familyTitle;
      this.productUrl = this.dataset.productUrl;
      this.currentOptionLabel = this.dataset.currentOption || 'Current option';
      this.currentPath = this.normalizePath(this.productUrl);
      this.optionsList = this.querySelector('[data-product-family-options-list]');
      this.status = this.querySelector('[data-product-family-status]');
      this.productInfo = this.closest('product-info');
      this.abortController = null;
      this.familyOptionsCache = window.__productFamilyOptionsCache || (window.__productFamilyOptionsCache = {});
      this.handleChipClick = this.handleChipClick.bind(this);

      this.addEventListener('click', this.handleChipClick);

      if (!this.optionsList || !this.familyKey || !this.familyTitle || !window.routes?.predictive_search_url) return;

      const cachedOptions = this.familyOptionsCache[this.familyKey];
      if (cachedOptions) {
        this.renderOptions(cachedOptions);
        return;
      }

      this.loadSiblingOptions();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleChipClick);
      this.abortController?.abort();
    }

    normalizePath(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        return parsed.pathname.replace(/\/+$/, '') || '/';
      } catch (error) {
        return url;
      }
    }

    buildCanonicalUrl(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        return parsed.pathname.replace(/\/+$/, '') || '/';
      } catch (error) {
        return url;
      }
    }

    setLoadingState(isLoading, activeChip = null) {
      this.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      this.querySelectorAll('.product-family-selector__chip').forEach((chip) => {
        chip.classList.toggle('is-loading', isLoading && chip === activeChip);
        chip.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      });
    }

    renderOptions({ options = [], moreText = '', shouldHide = false } = {}) {
      if (!this.optionsList) return;

      if (shouldHide || options.length <= 1) {
        this.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();
      let matchedCurrentChip = false;

      options.forEach(({ href, label }) => {
        if (!href || !label) return;

        const chip = document.createElement('a');
        chip.href = this.buildCanonicalUrl(href);
        chip.className = 'product-family-selector__chip';
        chip.textContent = label;

        if (this.normalizePath(href) === this.currentPath) {
          chip.classList.add('is-active');
          chip.setAttribute('aria-current', 'page');
          matchedCurrentChip = true;
        }

        fragment.appendChild(chip);
      });

      if (!matchedCurrentChip && this.currentOptionLabel) {
        const currentChip = document.createElement('a');
        currentChip.href = this.buildCanonicalUrl(this.productUrl);
        currentChip.className = 'product-family-selector__chip is-active';
        currentChip.setAttribute('aria-current', 'page');
        currentChip.textContent = this.currentOptionLabel;
        fragment.prepend(currentChip);
      }

      if (moreText) {
        const more = document.createElement('span');
        more.className = 'product-family-selector__more';
        more.textContent = moreText;
        fragment.appendChild(more);
      }

      this.optionsList.innerHTML = '';
      this.optionsList.appendChild(fragment);
      if (this.status) {
        this.status.hidden = true;
        this.status.textContent = '';
      }
      this.hidden = false;
    }

    async loadSiblingOptions() {
      const params = new URLSearchParams({
        q: this.familyTitle,
        section_id: 'predictive-search',
        'resources[type]': 'product',
        'resources[limit]': '25',
      });

      try {
        const response = await fetch(`${window.routes.predictive_search_url}?${params.toString()}`, {
          headers: {
            Accept: 'text/html',
          },
        });

        if (!response.ok) return;

        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const matchingFamily = doc.querySelector(
          `[data-predictive-family-key="${CSS.escape(this.familyKey)}"]`
        );

        if (!matchingFamily) return;

        const fetchedOptions = matchingFamily.querySelectorAll('.predictive-search__family-options a');
        if (!fetchedOptions.length) return;

        const seenPaths = new Set();
        const moreBadge = matchingFamily.querySelector('.predictive-search__family-more');
        const options = [];

        fetchedOptions.forEach((optionLink) => {
          const href = optionLink.getAttribute('href');
          const textContent = optionLink.textContent.trim();

          if (!href || !textContent) return;

          const normalizedHref = this.normalizePath(href);
          if (seenPaths.has(normalizedHref)) return;

          seenPaths.add(normalizedHref);
          options.push({
            href: this.buildCanonicalUrl(href),
            label: textContent,
          });
        });

        const payload = {
          options,
          moreText: moreBadge ? moreBadge.textContent.trim() : '',
          shouldHide: seenPaths.size <= 1,
        };
        this.familyOptionsCache[this.familyKey] = payload;
        this.renderOptions(payload);
      } catch (error) {
        if (this.status) {
          this.status.hidden = false;
          this.status.textContent = 'Other options are not available right now.';
        }
      }
    }

    handleChipClick(event) {
      const chip = event.target.closest('.product-family-selector__chip[href]');
      if (!chip || !this.contains(chip)) return;

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      const targetUrl = chip.href;
      if (!targetUrl || !this.productInfo) return;

      if (this.normalizePath(targetUrl) === this.currentPath) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      this.swapProduct(targetUrl, chip);
    }

    async swapProduct(targetUrl, chip) {
      const canonicalTargetUrl = this.buildCanonicalUrl(targetUrl);
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setLoadingState(true, chip);

      try {
        const response = await fetch(canonicalTargetUrl, {
          signal: this.abortController.signal,
          headers: {
            Accept: 'text/html',
          },
        });

        if (!response.ok) throw new Error(`Unable to load ${canonicalTargetUrl}`);

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        const nextTitle = html.querySelector('title');
        if (nextTitle) {
          document.title = nextTitle.textContent;
        }

        if (typeof this.productInfo.handleSwapProduct === 'function') {
          this.productInfo.handleSwapProduct(canonicalTargetUrl, false)(html);
        } else {
          const nextProductInfo = html.querySelector("product-info[id^='MainProduct']");
          if (!nextProductInfo) throw new Error('Product section not found.');

          HTMLUpdateUtility.viewTransition(this.productInfo, nextProductInfo);
          window.history.replaceState({}, '', canonicalTargetUrl);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.assign(canonicalTargetUrl);
      } finally {
        if (this.isConnected) this.setLoadingState(false);
      }
    }
  }

  customElements.define('product-family-selector', ProductFamilySelector);
}
